import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';

// Import CSS files
import './App.css';
import './index.css';
import './components/ChatWindow.css';
import './components/LoadingIndicator.css';
import './components/SkeletonLoader.css';
import './components/ThinkingIndicator.css';

interface XTMAssistantConfig {
  containerId?: string;
  apiUrl?: string;
  theme?: 'light' | 'dark';
  sessionId?: string;
  params?: {
    system_type?: string;
    [key: string]: any;
  };
}

export class XTMAssistant {
  private container: HTMLElement | null = null;
  private root: ReturnType<typeof createRoot> | null = null;
  private config: XTMAssistantConfig;
  private observer: MutationObserver | null = null;

  constructor(config: XTMAssistantConfig = {}) {
    this.config = {
      containerId: 'xtm-assistant-container',
      apiUrl: process.env.REACT_APP_API_URL,
      theme: 'light',
      sessionId: undefined,
      params: {},
      ...config
    };
  }

  mount(): void {
    // Create container if it doesn't exist
    if (!document.getElementById(this.config.containerId!)) {
      this.container = document.createElement('div');
      this.container.id = this.config.containerId!;
      document.body.appendChild(this.container);
    } else {
      this.container = document.getElementById(this.config.containerId!);
    }

    if (!this.container) {
      console.error('Could not find or create container element');
      return;
    }

    // Apply some basic styling to the container
    this.container.style.position = 'fixed';
    this.container.style.bottom = '0';
    this.container.style.right = '20px';
    this.container.style.zIndex = '9999';

    // Create Shadow DOM for style isolation
    const shadowRoot = this.container.attachShadow({ mode: 'open' });

    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Import fonts */
      @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&family=Titillium+Web:wght@400;600&display=swap');
    `;
    shadowRoot.appendChild(styleElement);

    const appStyleElement = document.createElement('style');

    const styleElements = document.querySelectorAll('style');
    let combinedStyles = '';

    styleElements.forEach(element => {
      combinedStyles += element.textContent;
    });

    const linkElements = document.querySelectorAll('link[rel="stylesheet"]');

    linkElements.forEach(link => {
      const shadowLink = document.createElement('link');
      shadowLink.rel = 'stylesheet';
      shadowLink.href = (link as HTMLLinkElement).href ;
      shadowRoot.appendChild(shadowLink);
    });

    appStyleElement.textContent = combinedStyles;

    shadowRoot.appendChild(appStyleElement);

    this.observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeName === 'STYLE') {
            const newStyle = document.createElement('style');
            newStyle.textContent = (node as HTMLStyleElement).textContent;
            shadowRoot.appendChild(newStyle);
          }
          else if (node.nodeName === 'LINK' && (node as HTMLLinkElement).rel === 'stylesheet') {
            const newLink = document.createElement('link');
            newLink.rel = 'stylesheet';
            newLink.href = (node as HTMLLinkElement).href;
            shadowRoot.appendChild(newLink);
          }
        });
      });
    });

    this.observer.observe(document.head, { childList: true });

    const shadowContainer = document.createElement('div');
    shadowContainer.id = 'xtm-assistant-shadow-container';
    shadowRoot.appendChild(shadowContainer);

    this.root = createRoot(shadowContainer);
    this.root.render(
      <React.StrictMode>
        <App 
          apiUrl={this.config.apiUrl}
          sessionId={this.config.sessionId}
          params={this.config.params}
        />
      </React.StrictMode>
    );
  }

  unmount(): void {
    // Disconnect the observer to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }
}

// Export the class to make it available globally
(window as any).XTMAssistant = XTMAssistant;
